import type {
  BlockContent,
  CategoryProductData,
  CategoryProductIntent,
  CategoryProductItem,
  CategoryProductLink,
  MessageButton,
} from '../../../../types/chat-message';

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const firstValue = (source: UnknownRecord, keys: string[]): unknown => {
  for (const key of keys) {
    const value = source[key];
    if (value !== null && value !== undefined) return value;
  }
  return undefined;
};

const cleanString = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  return text || undefined;
};

const stringFrom = (source: UnknownRecord, keys: string[]) => (
  cleanString(firstValue(source, keys))
);

const numberFrom = (source: UnknownRecord, keys: string[]): number | undefined => {
  const value = firstValue(source, keys);
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return undefined;
  const parsed = Number(value.replace(/[.,\s]/g, ''));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const integerFrom = (source: UnknownRecord, keys: string[]) => {
  const value = numberFrom(source, keys);
  return value === undefined ? undefined : Math.trunc(value);
};

const imageFrom = (source: UnknownRecord): string | undefined => {
  const direct = stringFrom(source, ['imageUrl', 'image_url', 'image']);
  if (direct) return direct;
  const images = firstValue(source, ['imageUrls', 'image_urls']);
  return Array.isArray(images) ? cleanString(images[0]) : undefined;
};

const failureFrom = (source: UnknownRecord) => stringFrom(source, [
  'errorMessage', 'error_message', 'failureReason', 'failure_reason', 'reason', 'error',
]);

const statusFromText = (value: string): boolean | undefined => {
  const status = value.trim().toUpperCase();
  if (!status) return undefined;
  return ['SUCCESS', 'SUCCEEDED', 'OK', 'TRUE', '1'].includes(status);
};

const statusFrom = (source: UnknownRecord): boolean | undefined => {
  const value = firstValue(source, ['status', 'isSuccess', 'is_success']);
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === 0) return value === 1;
  if (typeof value === 'string') return statusFromText(value);
  return failureFrom(source) ? false : undefined;
};

const parseProduct = (value: unknown): CategoryProductItem | null => {
  if (!isRecord(value)) return null;
  const name = stringFrom(value, ['name', 'productName', 'product_name']);
  if (!name) return null;
  return buildProduct(value, name);
};

const buildProduct = (source: UnknownRecord, name: string): CategoryProductItem => ({
  id: stringFrom(source, ['id', 'productId', 'product_id']),
  name,
  imageUrl: imageFrom(source),
  sellingPrice: numberFrom(source, ['sellingPrice', 'selling_price', 'price']),
  totalRevenue: numberFrom(source, ['totalRevenue', 'total_revenue', 'revenue', 'totalSales', 'total_sales']),
  status: statusFrom(source),
  failureReason: failureFrom(source),
  orderNumber: integerFrom(source, ['orderNumber', 'order_number', 'order', 'stt', 'index']),
});

const listFromPayload = (payload: UnknownRecord): unknown[] => {
  const value = firstValue(payload, ['products', 'items', 'data', 'featuredProducts', 'featured_products']);
  return Array.isArray(value) ? value : [];
};

const payloadOf = (block: BlockContent): UnknownRecord => (
  isRecord(block.payload) ? block.payload : {}
);

const blockOrder = (block: BlockContent): number => {
  const order = payloadOf(block).order;
  return typeof order === 'number' ? order : 0;
};

const linkUrl = (value: unknown): string | undefined => {
  const url = cleanString(value);
  if (!url) return undefined;
  const markdownUrl = url.match(/^\[[^\]]*\]\((.*?)\)$/);
  return (markdownUrl ? markdownUrl[1] : url).trim();
};

const parseLink = (block: BlockContent): CategoryProductLink | null => {
  const payload = payloadOf(block);
  const text = cleanString(payload.text);
  const url = linkUrl(payload.url);
  return text || url ? { text: text || '', url: url || '' } : null;
};

const parseButtons = (payload: UnknownRecord): MessageButton[] => {
  if (!Array.isArray(payload.buttons)) return [];
  return payload.buttons.filter(isRecord).filter((item) => cleanString(item.name)) as unknown as MessageButton[];
};

const isProductBlock = (block: BlockContent) => (
  block.type.toUpperCase() === 'PRODUCT_LIST' || block.type.toUpperCase() === 'CATEGORY_PRODUCT'
);

export const isCategoryProductIntent = (
  intent?: string,
): intent is CategoryProductIntent => {
  const value = intent?.trim().toLowerCase();
  return value === 'analyze_category' || value === 'suggest_product';
};

const collectProducts = (blocks: BlockContent[]): CategoryProductItem[] => {
  const productBlock = blocks.find(isProductBlock);
  if (!productBlock) return [];
  const products = listFromPayload(payloadOf(productBlock))
    .map(parseProduct).filter((item): item is CategoryProductItem => !!item);
  if (!products.some((item) => item.status !== undefined)) return products;
  return products.map((item) => item.status === undefined ? { ...item, status: true } : item);
};

const collectLinks = (blocks: BlockContent[]): CategoryProductLink[] => (
  blocks.filter((block) => block.type.toUpperCase() === 'LINK')
    .map(parseLink).filter((link): link is CategoryProductLink => !!link)
);

const textParts = (blocks: BlockContent[]) => blocks
  .filter((block) => block.type.toUpperCase() === 'TEXT')
  .map((block) => ({ text: cleanString(payloadOf(block).text), payload: payloadOf(block) }));

const createResult = (blocks: BlockContent[], products: CategoryProductItem[]): CategoryProductData => {
  const texts = textParts(blocks);
  return {
    label: texts[0]?.text || '', products,
    footerText: texts.slice(1).map((item) => item.text).filter(Boolean).join('\n') || undefined,
    links: collectLinks(blocks),
    buttons: texts.flatMap((item) => parseButtons(item.payload)),
  };
};

export const parseCategoryProductBlocks = (
  intent: string | undefined,
  blockContents: BlockContent[] | undefined,
): CategoryProductData | null => {
  if (!isCategoryProductIntent(intent) || !blockContents?.length) return null;
  const sorted = [...blockContents].sort((a, b) => blockOrder(a) - blockOrder(b));
  const products = collectProducts(sorted);
  return products.length ? createResult(sorted, products) : null;
};

const displayMoney = (value?: number) => (
  value === undefined ? '--' : `${new Intl.NumberFormat('vi-VN').format(value)} ₫`
);

export const formatCategoryProductCopy = (data: CategoryProductData): string => {
  const products = data.products.map((item, index) => [
    `${item.orderNumber ?? index + 1}. ${item.name}`,
    `Giá bán: ${displayMoney(item.sellingPrice)}`,
    `Doanh số tổng: ${displayMoney(item.totalRevenue)}`,
    item.failureReason,
  ].filter(Boolean).join('\n'));
  return [data.label, ...products, data.footerText, ...data.links.map((link) => link.text || link.url)]
    .filter(Boolean).join('\n\n');
};
